/* eslint-disable no-invalid-this */
/* eslint-disable max-statements */

import ApiService, { INTERVENERS_API_CONTEXT, INTERVENERS_API_HOST } from './ApiService';
import { download } from './helpers';

/**
 * @override
 */
class IntervenersService extends ApiService {
    /**
     * constructor
     */
    constructor() {
        super({ baseURL: INTERVENERS_API_HOST });
    }
    /**
     * Get interveners list
     * @param {Object} sort Sort
     * @param {Object} config Axios config
     * @return {Object} Response with interveners
     */
    getInterveners = (sort, config) => {
        if (sort.trim() !== '') {
            return this.getList(`${INTERVENERS_API_CONTEXT}/interveners?$orderby=${sort}`, config);
        } else {
            return this.getList(`${INTERVENERS_API_CONTEXT}/interveners`, config);
        }
    };

    /**
     * Get interveners in file
     * @param {String} mimetype mime type
     * @param {String} filename Filename
     * @param {Object} config Axios config
     * @return {Object} Response with account
     */
    downloadInterveners = (mimetype, filename = null, config = {}) => {
        Object.assign(config || {}, { headers: { Accept: mimetype } });
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners`, config)
            .then(response => download(response, filename));
    };

    /**
     * Get intervener
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener
     */
    getIntervenersByUUIId = (uuid, config) => this.get(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}`, config);

    /**
     * Get intervener in file
     * @param {String} uuid Account UUID
     * @param {String} mimetype mime type
     * @param {String} filename Filename
     * @param {Object} config Axios config
     * @return {Object} Response with account
     */
    downloadIntervenersByUUId = (uuid, mimetype, filename = null, config = {}) => {
        Object.assign(config || {}, { headers: { Accept: mimetype } });
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}`, config)
            .then(response => download(response, filename));
    };

    /**
     * @param {String} url Url
     * @param {Object} config Axios config
     * @return {Object} Response with list of interveners
     */
    getPagination(url, config) {
         return this.get(`${url}`, config)
    }
    /**
     * Actualizamos la información del usuario
     * @param {Object} intervener Intervener data
     * @return {Object} Response of put operation
     */
    update = intervener => {
        intervener.uuid_group = undefined;
        intervener.uuid_permission_set = undefined;
        intervener._links._self = undefined;
        intervener.permission_set = undefined;
        intervener.group = undefined;
        return this.put(`${INTERVENERS_API_CONTEXT}/interveners/${intervener.uuid}`, intervener);
    };
    /**
     * Delete intervener
     * @param {String} uuid Intervener id
     * @return {Object} Response of delete operation
     */
    deleteByUUIId = uuid => {
        return this.delete(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}`, {});
    };
    /**
     * Get intervener Documents
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener documents data
     */
    getIntervenerDocuments = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/documents`, config);
    };
    /**
     * Get intervener
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener historic data
     */
    getIntervenerHistoric = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/historical`, config);
    };
    /**
     * Get intervener
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener operations data
     */
    getIntervenerOperations = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/operations`, config);
    };
    /**
     * Get intervener
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener operations data
     */
    getIntervenerParticipantAccounts = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/accounts`, config);
    };
    /**
     * Get intervener
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener operations data
     */
    getIntervenerIssues = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/issues`, config);
    };
    /**
     * Get intervener kyc data
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener kyc data
     */
    getIntervenerKyc = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/kyc`, config);
    };
    /**
     * Get intervener legal data
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener legal data
     */
    getIntervenerLegal = (uuid, config) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/business`, config);
    };
    /**
     * Get intervener crs data
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener legal data
     */
    getIntervenerCrs = (uuid, config) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/crs`, config);
    };
    /**
     * Get intervener fatca data
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener legal data
     */
    getIntervenerFatca = (uuid, config) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/fatca`, config);
    };
    /**
     * Get intervener GDPR data
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener legal data
     */
    getIntervenerGdpr = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/gdpr`, config);
    };

    /**
     * Buscar interveners
     * @param {Array} search Búsqueda
     * @param {Object} config Axios config
     * @param {Object} sort Sort
     * @param {Object} filter Filter
     * @return {Object} Response with users
     */
    getSearch = (search, config, sort = false, filter = null) => {
        const sortQueryParam = sort ? `&$orderby=${sort}` : '';
        const filterQueryParam = filter ? `&$filter=${filter}` : '';
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners?$search=${search}${sortQueryParam}${filterQueryParam}`, config);
    }
    /**
     * @param {String} uuid Intervener UUID
     * @param {String} uuidDocument Document UUID
     * @return {Object} Response with the document
     */
    getDocument = (uuid, uuidDocument) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/documents/${uuidDocument}`, { responseType: 'blob' })
            .then(res => res.data);
    };

    /**
     * Get intervener Documents
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener documents data
     */
    getIntervenerDocumentsValidations = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/document-validations`, config);
    };

    /**
     * Get categories document
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener categories data
     */
    getIntervenerCategories = (uuid, config) => {
        return this.getList(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/document-categories`, config);
    };

    /**
     * @override
     */
    download = (sort, config, headers) => {
        if (sort.trim() !== '') {
            return this.getDownload(`${INTERVENERS_API_CONTEXT}/interveners/download?$orderby=${sort}`, config, { headers });
        } else {
            return this.getDownload(`${INTERVENERS_API_CONTEXT}/interveners/download`, config, { headers });
        }
    };

    /**
     * Change document objects
     * @param {String} uuid UUID intervener
     * @param {String} uuidDocuemnt UUID document
     * @param {Object} payload modified properties
     * @return {Object} Response data
     */
    updateDocument = (uuid, uuidDocuemnt, payload) => {
        return this.patch(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/documents/${uuidDocuemnt}`, payload);
    };

    /**
     * @override
     */
    upload = payload => {

        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };

        const formData = new FormData();
        for (let i = 0; i < payload.files.length; i++) {
            formData.append('attachment', payload.files[i]);
            formData.append('filename', payload.filesName[i]);
            formData.append('category', (typeof payload.category === 'string') ? payload.category : payload.category[i]);
            formData.append('subcategory', (typeof payload.subcategory === 'string') ? payload.subcategory : payload.subcategory[i]);
            formData.append('validated', payload.validated);
        }
        return this.post(`${INTERVENERS_API_CONTEXT}/interveners/${payload.uuid}/documents`, formData, config);
    };

    /**
     * Update intervener info
     * @param {String} uuid UUID of intervener
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    updateIntervenerInfo = (uuid, payload) => {
        return this.patch(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}`, payload);
    };

    /**
     * Update intervener legal
     * @param {String} uuid UUID of intervener
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    updateIntervenerBusiness = (uuid, payload) => {
        return this.patch(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/business`, payload);
    };

    /**
     * Update intervener CRS
     * @param {String} uuid UUID of intervener
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    updateIntervenerCRS = (uuid, payload) => {
        return this.patch(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/crs`, payload);
    };

    /**
     * Update intervener FATCA
     * @param {String} uuid UUID of intervener
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    updateIntervenerFATCA = (uuid, payload) => {
        return this.patch(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/fatca`, payload);
    };

    /**
     * Update intervener GDPR
     * @param {String} uuid UUID of intervener
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    updateIntervenerGDPR = (uuid, payload) => {
        return this.patch(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/gdpr`, payload);
    };

    /**
     * Update intervener Address
     * @param {String} uuid UUID of intervener
     * @param {String} uuid_address UUID of address to update
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    updateIntervenerAddress = (uuid, uuid_address, payload) => {
        return this.patch(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/addresses/${uuid_address}`, payload);
    };

    /**
     * Update intervener Address
     * @param {String} uuid UUID of intervener
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    createIntervenerAddress = (uuid, payload) => {
        return this.post(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/addresses`, payload);
    };

    /**
     * Remove intervener Address
     * @param {String} uuid UUID of intervener
     * @param {String} uuid_address UUID of address to update
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    removeIntervenerAddress = (uuid, uuid_address) => {
        return this.delete(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/addresses/${uuid_address}`);
    };

    /**
     * Update intervener GDPR
     * @param {String} uuid UUID of intervener
     * @param {String} uuid_address UUID of address to update
     * @param {Object} payload modified properties
     * @return {Object} API response
     */
    getAddresses = uuid => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/addresses`);
    };

    /**
     * Get intervener fatca data
     * @param {String} uuid_intervener Intervener UUID
     * @param {String} uuidDocuemnt Document UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener legal data
     */
    removeDocument = (uuid_intervener, uuidDocuemnt, config) => {
        return this.delete(`${INTERVENERS_API_CONTEXT}/interveners/${uuid_intervener}/documents/${uuidDocuemnt}`, config);
    };

    /**
     * Reject intervener VideoID and send new link
     * @param {String} uuid UUID intervener
     * @return {Object} Response data
     */
    rejectVideoId = (uuid) => {
        return this.post(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/finveris/refresh`, { mode: 'reject' });
    };

    /**
     * Send VideoID link to intervener
     * @param {String} uuid UUID intervener
     * @return {Object} Response data
     */
    sendVideoId = (uuid) => {
        return this.post(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/finveris/refresh`, { mode: 'send' });
    };

    /**
     * Change intervener status
     * @param {String} uuid Intervener UUID
     * @param {Object} body body of request
     * @return {Object} Response with intervener changed
     */
    changeIntervenerStatus = (uuid, body) => {
        return this.post(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/status-change`, body);
    };

    /**
     * Send to sign with Finveris
     * @param {Object} body body of request
     * @return {Object} Response with intervener changed
     */
    documentSignatures = body => this.post(`${INTERVENERS_API_CONTEXT}/documents-signatures`, body);

    /**
     * Envia a RD
     * @param {String} uuid Intervener UUID
     * @return {Object} API Response
     */
    sendToRD = uuid => {
        return this.post(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/export-to-rd`);
    };


    sendMail = (mail, uri) => {
        return this.post(`${INTERVENERS_API_CONTEXT}/mail/send`, { mail, uri });
    };

    /**
     * Get intervener Balance
     * @param {String} uuid Intervener UUID
     * @param {Object} config Axios config
     * @return {Object} API Response
     */
    getIntervenerBalance = (uuid, config) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${uuid}/balance`, config);
    };

    /**
     * Calls an external service to validate a TIN number
     * @param {String} countryCode Country code
     * @param {String} tinNumber Tin number
     * @param {Object} config Axios config
     * @return {Object} API Response
     */
    validateTinNumber = (countryCode, tinNumber, config) => {
        return this.post(`${INTERVENERS_API_CONTEXT}/tin-validator`, { countryCode, tinNumber }, config);
    };

    /**
     * Get Max contribution limit
     * @param {String} intervener_id UUID or DNI
     * @param {Object} config Axios config
     * @return {Object} API Response
     */
    getMaxYearContribution = (intervener_id, config) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${intervener_id}/max-contribution`, config);
    };
    /**
     *
     * @param {String} intervener_uuid UUID
     * @param {Object} document_uuid id Document
     * @return {Object} Promise
     */
    downloadIntervenerDocument = (intervener_uuid, document_uuid) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${intervener_uuid}/documents/${document_uuid}`, { responseType: 'blob' });
    }
    /**
     *
     * @param {String} intervener_uuid UUID
     * @return {Object} Promise
     */
    downloadIntervenerDocuments = intervener_uuid => {
        return this.get(`${INTERVENERS_API_CONTEXT}/interveners/${intervener_uuid}/documents`, { responseType: 'blob' });
    }
    /**
     *
     * @param {String} intervener_uuid UUID
     * @return {Object} Promise
     */
    getIntervenerAml = ( intervener_uuid ) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/aml/interveners/${intervener_uuid}`);
    };
      /**
     *
     * @param {String} accountUuid UUID
     * @return {Object} Promise
     */
    getAccountAml = ( accountUuid ) => {
        return this.get(`${INTERVENERS_API_CONTEXT}/aml/accounts/${ accountUuid }`);
    };

    generateRecoveryLink(uuidIntervener ) {
        return this.post(`${INTERVENERS_API_CONTEXT}/interveners/${ uuidIntervener }/password-recover-url`);
    }

    createOTP = (config, body, intervenerUUID) => {
        return new Promise((resolve, reject) => {
            const hasPhone = body && !!body.mobile_phone && !!body.mobile_phone_prefix;
            const hasEmail = body && !!body.email;

            if (hasPhone || hasEmail) {
                resolve(this.post(
                `${INTERVENERS_API_CONTEXT}/otp/${intervenerUUID}`,
                body,
                config
                ));
            }
            else {
                reject({"valid": false, "errors": ["INVALID_OTP_BODY"]});
            }
            });
        }
    }

const intervenerService = new IntervenersService();

export default intervenerService;
